import { Component, type ErrorInfo, type ReactNode } from 'react'

/** Keeps one bad value from blanking the entire application.
 *
 *  Every screen here renders untrusted model output. A single unexpected value
 *  - a currency field holding '₹' rather than 'INR', say - can throw inside a
 *  browser API, and without a boundary React unmounts the whole tree and the
 *  user gets a black page with nothing to act on.
 */
interface Props { children: ReactNode; label?: string }
interface State { error: Error | null }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Replace with Sentry.captureException in production.
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="card" style={{ margin: 22, maxWidth: 620 }}>
        <div className="card-head">
          <span className="card-title" style={{ color: 'var(--crit)' }}>
            This screen could not be displayed
          </span>
        </div>
        <div className="card-body">
          <p style={{ marginTop: 0, color: 'var(--text-2)' }}>
            Something in the data on this page could not be rendered. Your work is
            saved — the problem is in how it was displayed, not in what was stored.
          </p>
          <pre style={{
            background: 'var(--surface-2)', border: '1px solid var(--line)',
            borderRadius: 'var(--radius-sm)', padding: '10px 12px',
            fontSize: 12, overflowX: 'auto', color: 'var(--crit)',
          }}>{error.message}</pre>
          <div className="row">
            <button
              className="btn btn-primary"
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
            <button className="btn" onClick={() => { window.location.href = '/inbox' }}>
              Back to the inbox
            </button>
          </div>
        </div>
      </div>
    )
  }
}
